// @givmo/app-charity — the unified statement's view model (CPA-WEB11).
//
// Turns ONE server response into the four groupings the table renders, plus the search/sort/filter
// state applied over them. Pure functions, no React — so the invariants below are unit-testable
// without rendering anything.
//
// ★ THE CLIENT NEVER SUMS MONEY. Every figure this module hands the renderer is a decimal STRING
// lifted verbatim out of the response: a group's totals come from that group's own server block, a
// nested bucket's from `buckets.grant_fundraiser` / `buckets.grant_direct`, the unshared bucket's from
// `buckets.unshared`. There is exactly ONE place money becomes a number — `orderingCents`, used to
// COMPARE two amounts when sorting — and its result is never rendered, never accumulated, and never
// written back onto a row.
//
// ★ A FILTERED GROUP KEEPS ITS TRUE TOTAL. Searching or chip-filtering hides donation rows; it never
// touches a group's money. A group showing fewer children than it has therefore says so explicitly
// ("2 of 20 shown") rather than quietly implying its total belongs to the rows on screen. The SAME
// grammar covers the recent-N window (a grant whose donations all fall outside it reads "0 of 37
// shown" under its real totals), so one rule serves both and neither invents a number.
//
// ★ THREE OF THE FOUR VIEWS ARE COMPLETE PARTITIONS — All, By grant and By donor each render every
// donation, which is why their remainder buckets are load-bearing rather than decorative: "Direct
// donations" inside a grant, and "Details not shared" under By donor. Dropping either would show a
// fraction of the money while the tiles above showed all of it.
//
// BY FUNDRAISER IS DELIBERATELY A SUBSET: fundraiser-attributed legs only, no direct rows and no
// Direct bucket (the ratified design is explicit about this). Its notice line names the charity-wide
// total so the view cannot be read as the whole. `buckets.fundraiser_direct` is the complement the
// server emits for it and is intentionally NOT rendered — do not "complete" this view with it, and do
// not derive the attributed total from it (that is client money arithmetic).

import type {
  Section321StatementDonationOut,
  Section321StatementFundraiserOut,
  Section321StatementGrantOut,
  Section321StatementOut,
  Section321StatementTotalsOut,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { grantStatusTone } from './grants-decode';
import {
  donationMatchesChip,
  grantRowExplainer,
  type StatementChip,
  type StatementSort,
  type StatementTab,
  DIRECT_BUCKET_LABEL,
  UNSHARED_EXPLAINER,
  UNSHARED_EXPLAINER_TERMS_NOT_ACCEPTED,
  UNSHARED_GROUP_LABEL,
} from './statement-vocab';

type BadgeTone = NonNullable<BadgeProps['tone']>;

/** The sentinel `grant_ref` the server uses for a donation with no grant record yet. */
export const UNASSIGNED_GRANT_REF = 'unassigned';

/**
 * What a group's name reads as when its ref cannot be resolved in the response — the same em dash
 * every other unknown on this surface uses. Reachable only on a wire inconsistency (a
 * `fundraiser_ref` absent from `fundraisers[]`), where a blank first cell would look like a rendering
 * bug rather than missing data.
 */
const UNKNOWN_LABEL = '—';

// ── The view model ──────────────────────────────────────────────────────────────────────────────────

export type GroupKind = 'grant' | 'fundraiser' | 'donor' | 'direct' | 'unshared';

export interface StatementGroup {
  /** Stable, unique across the whole view — the expansion key and the testid suffix. */
  key: string;
  kind: GroupKind;
  /**
   * The group's server-authored label. On a grant this is the SERVER's status-first label, rendered
   * verbatim as the badge in the grant row's Status cell (D-CP-55); on every other group it is the
   * row's identity.
   */
  label: string;
  /** `status` ⇒ the label is a status, a Badge in the Status column; `plain` ⇒ the row's identity, as text. */
  labelKind: 'status' | 'plain';
  /**
   * True on the one grant-kind group with no grant record — the server's `"unassigned"` sentinel
   * (`UNASSIGNED_GRANT_REF`). By grant names that row `UNASSIGNED_GRANT_LABEL`, never "Grant".
   * Decided on the wire's ref, never by matching the label text.
   */
  isUnassigned: boolean;
  tone: BadgeTone;
  /** "· bank transfer" / "· check" — server copy, only ever present on a sent grant. */
  method: string | null;
  /** Content for this row's (i), or null when the row needs no explainer. */
  explainer: string | null;
  /** The fundraiser's server-composed public link (the ↗), or null. */
  publicUrl: string | null;
  /** Recipient-relative ownership metadata; absent/unknown never opens management. */
  ownerKind?: 'org' | 'supporter' | null;
  /** A donor group's email, shown on the secondary line beside its count. */
  email: string | null;
  /** Set on a fundraiser group — the key the flag-gated message/organizer panel is fetched by. */
  fundraiserRef: string | null;
  /** The SERVER's aggregate for this group. Never recomputed, never adjusted for a filter. */
  totals: Section321StatementTotalsOut;
  /** Nested buckets (only inside a grant: its fundraiser buckets + the Direct remainder). */
  groups: StatementGroup[];
  /** Donation rows directly under this group. */
  donations: Section321StatementDonationOut[];
  /** Donation rows rendered anywhere beneath this group. Drives the "n of M shown" grammar. */
  shownCount: number;
  /** False on a grant with no donations at all — a $0 row with nothing to disclose. */
  toggleable: boolean;
}

/** A top-level entry: a group, or (in the All view) a bare donation row. */
export type StatementItem =
  | { kind: 'group'; group: StatementGroup }
  | { kind: 'donation'; donation: Section321StatementDonationOut };

export interface StatementView {
  items: StatementItem[];
  /** Every donation row rendered anywhere in this view. */
  shownCount: number;
  /** Expansion keys for every toggleable group, at every level — the expand-all target. */
  expandableKeys: string[];
}

export interface StatementFilters {
  tab: StatementTab;
  sort: StatementSort;
  /** Free-text search across donor name/email, designation, dedication, fundraiser name. */
  query: string;
  chips: readonly StatementChip[];
}

// ── Lookups ─────────────────────────────────────────────────────────────────────────────────────────

export interface StatementIndex {
  grants: Map<string, Section321StatementGrantOut>;
  fundraisers: Map<string, Section321StatementFundraiserOut>;
  /** `${grant_ref}\u0000${fundraiser_ref}` → the cross aggregate. */
  grantFundraiser: Map<string, Section321StatementTotalsOut>;
  grantDirect: Map<string, Section321StatementTotalsOut>;
}

export function indexStatement(statement: Section321StatementOut): StatementIndex {
  return {
    grants: new Map(statement.grants.map((grant) => [grant.ref, grant])),
    fundraisers: new Map(statement.fundraisers.map((f) => [f.ref, f])),
    grantFundraiser: new Map(
      statement.buckets.grant_fundraiser.map((b) => [
        crossKey(b.grant_ref, b.fundraiser_ref),
        b.totals,
      ]),
    ),
    grantDirect: new Map(statement.buckets.grant_direct.map((b) => [b.grant_ref, b.totals])),
  };
}

function crossKey(grantRef: string, fundraiserRef: string): string {
  return `${grantRef}\u0000${fundraiserRef}`;
}

// ── Ordering ────────────────────────────────────────────────────────────────────────────────────────

/**
 * A wire decimal string as integer CENTS — **for ordering only**.
 *
 * This is the one place on this surface where money becomes a number, and it is a COMPARISON, not a
 * sum: the value is used to decide which of two rows comes first and is then discarded. It is never
 * rendered (`displayMoney` formats the original string), never accumulated, and never stored on a row.
 * Anything unparseable returns null and sorts last, so an unexpected wire shape changes the ORDER of a
 * row rather than its content.
 */
export function orderingCents(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return null;
  const whole = Number(match[2]);
  const cents = Number((match[3] ?? '').slice(0, 2).padEnd(2, '0'));
  if (!Number.isFinite(whole) || !Number.isFinite(cents)) return null;
  const magnitude = whole * 100 + cents;
  return match[1] === '-' ? -magnitude : magnitude;
}

function instant(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Ascending compare that always sorts a null (unknown) key LAST, whichever direction is asked for. */
function compare(a: number | null, b: number | null, direction: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * direction;
}

function donationSortKey(
  donation: Section321StatementDonationOut,
  sort: StatementSort,
): number | null {
  switch (sort) {
    case 'newest':
    case 'oldest':
      return instant(donation.date_made);
    case 'amount-high':
    case 'amount-low':
      return orderingCents(donation.gross);
    case 'date-sent':
      return instant(donation.date_sent);
  }
}

function direction(sort: StatementSort): 1 | -1 {
  return sort === 'oldest' || sort === 'amount-low' ? 1 : -1;
}

function sortDonations(
  donations: Section321StatementDonationOut[],
  sort: StatementSort,
): Section321StatementDonationOut[] {
  const dir = direction(sort);
  return [...donations].sort((a, b) =>
    compare(donationSortKey(a, sort), donationSortKey(b, sort), dir),
  );
}

/**
 * A group's sort key, in the SAME dimension as its children — so choosing "Amount (high to low)"
 * orders the groups by total AND the donations inside each one by amount. One mental model.
 *
 * `hints` carry the server's whole-history instants (`first_donation`/`last_donation`) where the block
 * has them, and they are PREFERRED over anything derived from the rendered children: under the
 * recent-N window a group can render zero children while genuinely being the most recent thing on the
 * statement, and ordering it last would be wrong about the data rather than about the filter.
 */
function groupSortKey(
  group: StatementGroup,
  sort: StatementSort,
  hints: { first: string | null; last: string | null },
): number | null {
  switch (sort) {
    case 'newest':
      return instant(hints.last) ?? extremeDonationKey(group, sort, Math.max);
    case 'oldest':
      return instant(hints.first) ?? extremeDonationKey(group, sort, Math.min);
    case 'amount-high':
    case 'amount-low':
      return orderingCents(group.totals.gross_total);
    case 'date-sent':
      return extremeDonationKey(group, sort, Math.max);
  }
}

function extremeDonationKey(
  group: StatementGroup,
  sort: StatementSort,
  pick: (...values: number[]) => number,
): number | null {
  const keys: number[] = [];
  const walk = (node: StatementGroup) => {
    for (const donation of node.donations) {
      const key = donationSortKey(donation, sort);
      if (key !== null) keys.push(key);
    }
    node.groups.forEach(walk);
  };
  walk(group);
  return keys.length > 0 ? pick(...keys) : null;
}

// ── Filtering ───────────────────────────────────────────────────────────────────────────────────────

/** The haystack one donation is searched over — every field the toolbar's placeholder promises. */
function searchHaystack(donation: Section321StatementDonationOut, index: StatementIndex): string {
  const fundraiserName = donation.fundraiser_ref
    ? index.fundraisers.get(donation.fundraiser_ref)?.name
    : null;
  return [
    donation.donor_name,
    donation.donor_email,
    donation.designation,
    donation.dedication_name,
    fundraiserName,
  ]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join('\u0000')
    .toLowerCase();
}

/** True when a filter is actually narrowing the set — the switch between "keep every server group so
 *  a $0 grant still renders honestly" and "a group nothing matched disappears". */
export function isFiltering(filters: StatementFilters): boolean {
  return filters.query.trim().length > 0 || filters.chips.length > 0;
}

function visibleDonations(
  statement: Section321StatementOut,
  index: StatementIndex,
  filters: StatementFilters,
): Section321StatementDonationOut[] {
  const needle = filters.query.trim().toLowerCase();
  return statement.donations.filter((donation) => {
    if (needle.length > 0 && !searchHaystack(donation, index).includes(needle)) return false;
    return filters.chips.every((chip) => donationMatchesChip(donation, chip));
  });
}

// ── Group construction ──────────────────────────────────────────────────────────────────────────────

const EMPTY_TOTALS: Section321StatementTotalsOut = {
  donation_count: 0,
  gross_total: '0.00',
  net_total: '0.00',
  total_fee: '0.00',
};

function group(
  partial: Partial<StatementGroup> & Pick<StatementGroup, 'key' | 'kind' | 'label'>,
): StatementGroup {
  return {
    labelKind: 'plain',
    isUnassigned: false,
    tone: 'neutral',
    method: null,
    explainer: null,
    publicUrl: null,
    ownerKind: null,
    email: null,
    fundraiserRef: null,
    totals: EMPTY_TOTALS,
    groups: [],
    donations: [],
    shownCount: 0,
    toggleable: true,
    ...partial,
  };
}

/** Deep count of rendered donation rows, computed AFTER the tree is assembled. */
function seal(node: StatementGroup): StatementGroup {
  node.groups.forEach(seal);
  node.shownCount =
    node.donations.length + node.groups.reduce((sum, child) => sum + child.shownCount, 0);
  // A grant the server reports as having no donations at all has nothing to disclose: it renders at
  // $0 with its real status and NO toggle, rather than a chevron that opens onto nothing.
  if (node.kind === 'grant' && node.totals.donation_count === 0) node.toggleable = false;
  return node;
}

// ── The four groupings ──────────────────────────────────────────────────────────────────────────────

function buildByGrant(
  statement: Section321StatementOut,
  index: StatementIndex,
  donations: Section321StatementDonationOut[],
  filters: StatementFilters,
): StatementGroup[] {
  const filtering = isFiltering(filters);
  const byGrant = new Map<string, Section321StatementDonationOut[]>();
  for (const donation of donations) {
    const list = byGrant.get(donation.grant_ref);
    if (list) list.push(donation);
    else byGrant.set(donation.grant_ref, [donation]);
  }

  const out: StatementGroup[] = [];
  for (const grant of statement.grants) {
    const mine = byGrant.get(grant.ref) ?? [];
    if (filtering && mine.length === 0) continue;

    const node = group({
      key: `grant:${grant.ref}`,
      kind: 'grant',
      // The server's status-first label, exactly as worded — the badge in the row's Status cell.
      label: grant.label,
      labelKind: 'status',
      isUnassigned: grant.ref === UNASSIGNED_GRANT_REF,
      tone: grantStatusTone(grant.status),
      method: grant.send_method ?? null,
      explainer: grantRowExplainer(grant.status, grant.ref === UNASSIGNED_GRANT_REF),
      totals: grant.totals,
    });

    // Fundraiser buckets appear INSIDE a grant only where attribution exists; when they do, the
    // remaining legs get a peer "Direct donations" bucket so the hierarchy stays consistent. A grant
    // with no attributed legs keeps its donations as flat children — no pointless single bucket.
    const attributedRefs = [...new Set(mine.map((d) => d.fundraiser_ref).filter(isRef))];
    if (attributedRefs.length > 0) {
      for (const ref of attributedRefs) {
        const fundraiser = index.fundraisers.get(ref);
        const bucketTotals = index.grantFundraiser.get(crossKey(grant.ref, ref));
        node.groups.push(
          group({
            key: `grant:${grant.ref}:fundraiser:${ref}`,
            kind: 'fundraiser',
            label: fundraiser?.name ?? UNKNOWN_LABEL,
            publicUrl: fundraiser?.public_url ?? null,
            ownerKind: fundraiser?.owner_kind ?? null,
            fundraiserRef: ref,
            totals: bucketTotals ?? EMPTY_TOTALS,
            donations: mine.filter((d) => d.fundraiser_ref === ref),
          }),
        );
      }
      const direct = mine.filter((d) => !isRef(d.fundraiser_ref));
      const directTotals = index.grantDirect.get(grant.ref);
      // The remainder bucket renders whenever the SERVER emitted one, even if the window or the
      // filter left it with no visible rows — its total is real money inside this grant either way.
      if (direct.length > 0 || (directTotals && !filtering)) {
        node.groups.push(
          group({
            key: `grant:${grant.ref}:direct`,
            kind: 'direct',
            label: DIRECT_BUCKET_LABEL,
            totals: directTotals ?? EMPTY_TOTALS,
            donations: direct,
          }),
        );
      }
    } else {
      node.donations = mine;
    }
    out.push(seal(node));
  }
  return out;
}

function isRef(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function buildByFundraiser(
  statement: Section321StatementOut,
  donations: Section321StatementDonationOut[],
  filters: StatementFilters,
): StatementGroup[] {
  const filtering = isFiltering(filters);
  const out: StatementGroup[] = [];
  for (const fundraiser of statement.fundraisers) {
    const mine = donations.filter((d) => d.fundraiser_ref === fundraiser.ref);
    if (filtering && mine.length === 0) continue;
    out.push(
      seal(
        group({
          key: `fundraiser:${fundraiser.ref}`,
          kind: 'fundraiser',
          label: fundraiser.name,
          publicUrl: fundraiser.public_url,
          ownerKind: fundraiser.owner_kind ?? null,
          fundraiserRef: fundraiser.ref,
          totals: fundraiser.totals,
          donations: mine,
        }),
      ),
    );
  }
  return out;
}

function buildByDonor(
  statement: Section321StatementOut,
  donations: Section321StatementDonationOut[],
  filters: StatementFilters,
): StatementGroup[] {
  const filtering = isFiltering(filters);
  const out: StatementGroup[] = [];
  for (const donor of statement.donors) {
    const mine = donations.filter((d) => d.donor_ref === donor.ref);
    if (filtering && mine.length === 0) continue;
    out.push(
      seal(
        group({
          key: `donor:${donor.ref}`,
          kind: 'donor',
          label: donor.name ?? donor.email ?? '—',
          email: donor.name ? (donor.email ?? null) : null,
          totals: donor.totals,
          donations: mine,
        }),
      ),
    );
  }
  return out;
}

/**
 * The by-donor remainder. Money-complete by construction: every leg whose identity is not disclosable
 * lands here, so Σ donors + unshared is the charity total. It is always LAST — it is the absence of a
 * donor rather than one, so ordering it among named people by date or amount would read as a claim
 * about a person.
 */
function buildUnsharedGroup(
  statement: Section321StatementOut,
  donations: Section321StatementDonationOut[],
  filters: StatementFilters,
): StatementGroup | null {
  const mine = donations.filter((d) => !isRef(d.donor_ref));
  const totals = statement.buckets.unshared;
  if (isFiltering(filters) && mine.length === 0) return null;
  if (totals.donation_count === 0 && mine.length === 0) return null;
  return seal(
    group({
      key: 'unshared',
      kind: 'unshared',
      label: UNSHARED_GROUP_LABEL,
      // While the Terms are unaccepted the bucket holds every donation, for the organization's reason.
      explainer: donorGateClosed(statement)
        ? UNSHARED_EXPLAINER_TERMS_NOT_ACCEPTED
        : UNSHARED_EXPLAINER,
      totals,
      donations: mine,
    }),
  );
}

/**
 * The All view: ONE newest-first flat list. Each fundraiser is a single expandable row AT the top
 * level, positioned by its most recent donation; everything else is a bare donation row. There is
 * deliberately no "Direct donations" grouping here — in a flat list a bucket holding "everything that
 * isn't a fundraiser" is a container around the majority of the rows, which adds a level of nesting
 * and explains nothing.
 */
function buildAll(
  index: StatementIndex,
  donations: Section321StatementDonationOut[],
): { groups: StatementGroup[]; bare: Section321StatementDonationOut[] } {
  const groups: StatementGroup[] = [];
  const bare: Section321StatementDonationOut[] = [];
  const byFundraiser = new Map<string, Section321StatementDonationOut[]>();
  for (const donation of donations) {
    if (isRef(donation.fundraiser_ref)) {
      const list = byFundraiser.get(donation.fundraiser_ref);
      if (list) list.push(donation);
      else byFundraiser.set(donation.fundraiser_ref, [donation]);
    } else {
      bare.push(donation);
    }
  }
  for (const [ref, mine] of byFundraiser) {
    const fundraiser = index.fundraisers.get(ref);
    groups.push(
      seal(
        group({
          key: `all:fundraiser:${ref}`,
          kind: 'fundraiser',
          label: fundraiser?.name ?? UNKNOWN_LABEL,
          publicUrl: fundraiser?.public_url ?? null,
          ownerKind: fundraiser?.owner_kind ?? null,
          fundraiserRef: ref,
          totals: fundraiser?.totals ?? EMPTY_TOTALS,
          donations: mine,
        }),
      ),
    );
  }
  return { groups, bare };
}

// ── The entry point ─────────────────────────────────────────────────────────────────────────────────

export function buildStatementView(
  statement: Section321StatementOut,
  filters: StatementFilters,
  index: StatementIndex = indexStatement(statement),
): StatementView {
  const donations = visibleDonations(statement, index, filters);
  const dir = direction(filters.sort);

  let items: StatementItem[];
  let pinnedLast: StatementGroup | null = null;

  if (filters.tab === 'all') {
    const { groups, bare } = buildAll(index, donations);
    // Groups and bare donations interleave in ONE ordering — a fundraiser row sits wherever its
    // newest donation puts it, not above or below the loose rows as a class.
    const mixed: { key: number | null; item: StatementItem }[] = [
      ...groups.map((g) => ({
        key: groupSortKey(g, filters.sort, {
          first: index.fundraisers.get(g.fundraiserRef ?? '')?.first_donation ?? null,
          last: index.fundraisers.get(g.fundraiserRef ?? '')?.last_donation ?? null,
        }),
        item: { kind: 'group', group: g } as StatementItem,
      })),
      ...bare.map((donation) => ({
        key: donationSortKey(donation, filters.sort),
        item: { kind: 'donation', donation } as StatementItem,
      })),
    ];
    mixed.sort((a, b) => compare(a.key, b.key, dir));
    items = mixed.map((entry) => entry.item);
  } else {
    let groups: StatementGroup[];
    if (filters.tab === 'grant') {
      groups = buildByGrant(statement, index, donations, filters);
    } else if (filters.tab === 'fundraiser') {
      groups = buildByFundraiser(statement, donations, filters);
    } else {
      groups = buildByDonor(statement, donations, filters);
      pinnedLast = buildUnsharedGroup(statement, donations, filters);
    }
    groups.sort((a, b) =>
      compare(
        groupSortKey(a, filters.sort, hintsFor(a, statement, index)),
        groupSortKey(b, filters.sort, hintsFor(b, statement, index)),
        dir,
      ),
    );
    items = groups.map((group) => ({ kind: 'group', group }));
  }

  // Nested buckets and donation children are ordered in the same dimension, at every level.
  for (const item of items) {
    if (item.kind === 'group') orderWithin(item.group, filters.sort, statement, index);
  }
  if (pinnedLast) {
    orderWithin(pinnedLast, filters.sort, statement, index);
    items = [...items, { kind: 'group', group: pinnedLast }];
  }

  const shownCount = items.reduce(
    (sum, item) => sum + (item.kind === 'group' ? item.group.shownCount : 1),
    0,
  );
  return { items, shownCount, expandableKeys: collectExpandable(items) };
}

function hintsFor(
  node: StatementGroup,
  statement: Section321StatementOut,
  index: StatementIndex,
): { first: string | null; last: string | null } {
  if (node.kind === 'fundraiser' && node.fundraiserRef) {
    const fundraiser = index.fundraisers.get(node.fundraiserRef);
    return { first: fundraiser?.first_donation ?? null, last: fundraiser?.last_donation ?? null };
  }
  if (node.kind === 'donor') {
    const donor = statement.donors.find((d) => `donor:${d.ref}` === node.key);
    return { first: donor?.first_donation ?? null, last: donor?.last_donation ?? null };
  }
  // Grants, the in-grant buckets and the unshared bucket carry no whole-history instants, so their
  // order is derived from the donations actually on screen (which is what the design draws).
  return { first: null, last: null };
}

function orderWithin(
  node: StatementGroup,
  sort: StatementSort,
  statement: Section321StatementOut,
  index: StatementIndex,
): void {
  node.donations = sortDonations(node.donations, sort);
  node.groups.sort((a, b) =>
    compare(
      groupSortKey(a, sort, hintsFor(a, statement, index)),
      groupSortKey(b, sort, hintsFor(b, statement, index)),
      direction(sort),
    ),
  );
  node.groups.forEach((child) => orderWithin(child, sort, statement, index));
}

function collectExpandable(items: StatementItem[]): string[] {
  const keys: string[] = [];
  const walk = (node: StatementGroup) => {
    if (node.toggleable) keys.push(node.key);
    node.groups.forEach(walk);
  };
  for (const item of items) if (item.kind === 'group') walk(item.group);
  return keys;
}

// ── Secondary-line grammar ──────────────────────────────────────────────────────────────────────────

/**
 * The count line under a group's name.
 *
 * ONE rule covers both cases that hide children — an active search/filter, and the recent-N window:
 * whenever fewer rows are rendered than the server says the group holds, the line says so explicitly
 * instead of letting the group's (unchanged, true) total look like the sum of what is on screen.
 */
export function groupCountLabel(group: StatementGroup): string {
  const total = group.totals.donation_count;
  if (group.shownCount < total) return `${group.shownCount} of ${total} shown`;
  return `${total} ${total === 1 ? 'donation' : 'donations'}`;
}

// ── The organization's donor-identity gate (CPA-BE84) ───────────────────────────────────────────────

/**
 * Whether the organization's donor-identity gate is closed: it has not accepted the Charity Terms
 * (plan 18 §3.3). By donor's line and the "Details not shared" (i) say so in the console's own words
 * (`statement-vocab.ts`). The read serves the flag alone, never a sentence.
 *
 * Only the server's `consent_available === false` closes it. An older backend that serves no flag reads
 * as open, never as closed, because the words are false for a charity that has accepted. It is a fact
 * about the organization, never a row's reason.
 */
export function donorGateClosed(statement: Section321StatementOut): boolean {
  return statement.consent_available === false;
}
